import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, formatDate } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import StatusBadge from "@/components/StatusBadge";
import ConfirmDialog from "@/components/ConfirmDialog";
import { F } from "@/pages/Employees";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Eye, Unlock, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function PayrollHistory() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isSuper = user.role === "super_admin";
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [unlock, setUnlock] = useState(null);
  const [reason, setReason] = useState("");
  const [del, setDel] = useState(null);

  const load = async () => {
    const r = await api.get("/payroll/periods");
    setRows(r.data);
    const t = {};
    await Promise.all(r.data.map(async (p) => {
      const d = await api.get(`/payroll/periods/${p.id}/details`);
      t[p.id] = d.data.reduce((s, x) => s + Number(x.gaji_bersih || 0), 0);
    }));
    setTotals(t);
  };
  useEffect(() => { load(); }, []);

  const doUnlock = async () => {
    try { await api.post(`/payroll/periods/${unlock.id}/unlock`, { alasan: reason }); toast.success("Payroll di-unlock"); setUnlock(null); setReason(""); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/payroll/periods/${del.id}`); toast.success("Periode dihapus"); setDel(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const columns = [
    { key: "nama", label: "Periode", sortable: true, render: (r) => <span className="font-semibold">{r.nama}</span> },
    { key: "tanggal_mulai", label: "Tanggal", render: (r) => `${formatDate(r.tanggal_mulai)} - ${formatDate(r.tanggal_selesai)}` },
    { key: "jumlah_pegawai", label: "Pegawai", align: "right", render: (r) => r.jumlah_pegawai ?? 0 },
    { key: "total", label: "Total Payroll", align: "right", render: (r) => rupiah(totals[r.id]) },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    {
      key: "act", label: "Aksi", render: (r) => (
        <div className="flex gap-1">
          <Button size="icon" variant="ghost" data-testid={`hist-view-${r.id}`} onClick={() => nav("/payroll")}><Eye className="h-4 w-4" /></Button>
          {isSuper && ["FINAL", "DIBAYAR"].includes(r.status) && <Button size="icon" variant="ghost" className="text-blue-600" data-testid={`hist-unlock-${r.id}`} onClick={() => setUnlock(r)}><Unlock className="h-4 w-4" /></Button>}
          {!["FINAL", "DIBAYAR"].includes(r.status) && <Button size="icon" variant="ghost" className="text-red-600" data-testid={`hist-delete-${r.id}`} onClick={() => setDel(r)}><Trash2 className="h-4 w-4" /></Button>}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Riwayat & Lock Payroll" subtitle="Kelola status DRAFT / DIPERIKSA / FINAL / DIBAYAR" />
      <DataTable columns={columns} data={rows} searchKeys={["nama"]} testid="hist-table" />

      <Dialog open={!!unlock} onOpenChange={() => setUnlock(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Unlock Payroll — {unlock?.nama}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Unlock akan mengembalikan status ke DIPERIKSA dan tercatat di Audit Log.</p>
          <F label="Alasan Unlock *"><Textarea data-testid="unlock-reason" value={reason} onChange={(e) => setReason(e.target.value)} /></F>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnlock(null)}>Batal</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={doUnlock} data-testid="unlock-confirm" disabled={!reason}>Unlock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!del} onOpenChange={() => setDel(null)} destructive testid="hist-delete-dialog"
        title="Hapus Periode?" description={`Hapus periode ${del?.nama} beserta detail perhitungannya?`} confirmText="Hapus" onConfirm={doDelete} />
    </div>
  );
}
